import type { BgId, Hex, Widget } from '../contract.js';
import { color } from '../tokens.js';

/** Colour helpers and text backgrounds, ported from the prototype (04 §9; 02 §1.4). */

/** `rgba(r, g, b, a)` of a `#rrggbb` colour. */
export function hexA(hex: Hex, alpha: number): string {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${alpha})`;
}

/** Channel-wise mix of two `#rrggbb` colours; `t` = 0 gives `a`, 1 gives `b`. */
export function mixHex(a: Hex, b: Hex, t: number): Hex {
  const A = a.replace('#', ''), B = b.replace('#', '');
  let out = '#';
  for (let i = 0; i < 3; i += 1) {
    const x = parseInt(A.slice(i * 2, i * 2 + 2), 16), y = parseInt(B.slice(i * 2, i * 2 + 2), 16);
    out += `0${Math.round(x + (y - x) * t).toString(16)}`.slice(-2);
  }
  return out;
}

/** Luminance on gamma-encoded channels, as the prototype computes it (no linearisation). */
export function lum(hex: Hex): number {
  const h = hex.replace('#', '');
  return 0.2126 * (parseInt(h.slice(0, 2), 16) / 255) + 0.7152 * (parseInt(h.slice(2, 4), 16) / 255) + 0.0722 * (parseInt(h.slice(4, 6), 16) / 255);
}

export const isLightBg = (css: string): boolean => !!css && css.charAt(0) === '#' && lum(css) > 0.5;

export type GradientId = 'g:own' | 'g:dusk' | 'g:ember' | 'g:tide' | 'g:bloom' | 'g:meadow';
export const GRADIENT_IDS: readonly GradientId[] = ['g:own', 'g:dusk', 'g:ember', 'g:tide', 'g:bloom', 'g:meadow'];

/** The six gradients as [start, end]; `g:own` follows the owner's colour. */
export function grads(owner: Hex): Readonly<Record<GradientId, readonly [Hex, Hex]>> {
  return {
    'g:own': [owner, mixHex(owner, color.tile, 0.5)],
    'g:dusk': ['#2f3854', '#12161f'],
    'g:ember': ['#f0a26e', '#f08a7e'],
    'g:tide': ['#6aaef0', '#7fd4d0'],
    'g:bloom': ['#ee8fb8', '#b3a4f5'],
    'g:meadow': ['#86d4a0', '#e8c75f'],
  };
}

export interface BgSpec {
  /** CSS `background` value. */
  readonly css: string;
  /** Text on it is dark. */
  readonly light: boolean;
  /** Frost: the widget also gets `backdrop-filter: blur(14px) saturate(1.3)`. */
  readonly blur: boolean;
}

/** What a background id paints, for a grid owned by `owner` (`bgSpec`). */
export function bgSpec(id: string | null | undefined, owner: Hex): BgSpec {
  if (id === 'none') return { css: 'transparent', light: false, blur: false };
  if (id === 'tile') return { css: color.tile, light: false, blur: false };
  if (id === 'light') return { css: color.paper, light: true, blur: false };
  if (id === 'frost') return { css: 'rgba(233, 237, 245, 0.1)', light: false, blur: true };
  if (id === 'own') return { css: owner, light: isLightBg(owner), blur: false };
  const gradient = id ? (grads(owner) as Readonly<Record<string, readonly [Hex, Hex] | undefined>>)[id] : undefined;
  if (gradient) return { css: `linear-gradient(150deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`, light: (lum(gradient[0]) + lum(gradient[1])) / 2 > 0.5, blur: false };
  if (id && id.charAt(0) === '#') return { css: id, light: isLightBg(id), blur: false };
  return { css: color.tile, light: false, blur: false };
}
export const bgCss = (id: string | null | undefined, owner: Hex): string => bgSpec(id, owner).css;

/** The background a widget shows: its own, or the dark tile for text (`bgId`). */
export function bgId(item: Pick<Widget, 't' | 'bg'>): BgId | null {
  if (item.bg != null) return item.bg;
  return item.t === 'text' ? 'tile' : null;
}

/** Swatch preview of frost, which differs from what the widget paints (02 §1.4). */
export const FROST_SWATCH = 'linear-gradient(135deg, rgba(233, 237, 245, 0.45), rgba(233, 237, 245, 0.08))';
